import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { api } from '../../utils/api';
import { useStore } from '../../store';
import OverflowChips, { OChip } from '../OverflowChips';
import { OpenPeriodForm } from '../Bookkeeping/StatutoryBooks';
import type { AwSummary } from '../../types';
import { WORKSPACE_MENUS, resolveMenu, movedMenu, visibleSubs, AwMenu, AwSub, AwVisibility } from './menus';
import type { AwCtx, AwPeriod } from './ctx';
import Placeholder from './Placeholder';
import { ArApPane, DashCashFlowPane, OverviewPane, TaxDeadlinesPane } from './panes/Dashboard';
import { DocsPane } from './panes/Documents';
import { ProductsAccountsPane } from './panes/ProductsAccounts';
import { DebitCreditNotesPane, PurchaseOrdersPane, SupplierBillsPane } from './panes/Purchases';
import { ExpenseCategoriesPane, ReimbursementsPane } from './panes/Expenses';
import { ExpenseRecordsPane } from './panes/ExpenseRecords';
import { ExpensePaymentsPane } from './panes/ExpensePayments';
import { EmployeesPane } from './panes/PayrollEmployees';
import { PayrollSettingsPane } from './panes/PayrollSettings';
import { PayrollRunsPane } from './panes/PayrollRuns';
import { PayrollReportsPane } from './panes/PayrollReports';
import { BankAccountsPane, ReconciliationPane, TransactionsPane, TransfersPane } from './panes/Banking';
import { PettyCashPane } from './panes/PettyCash';
import { FixedAssetsPane, ChartPane, ClosingPane, JournalPane, LedgerPane, PeriodsPane } from './panes/Accounting';
import { PndPane, Pp30Pane, TaxReportsPane } from './panes/Tax';
import { ArApAgingPane, BalanceSheetPane, PlPane, ReportsCashFlowPane } from './panes/Reports';
import { CompanyProfilePane, LoginsPane, NumberingPane, TaxSettingsPane } from './panes/Settings';
import { PostingAccountsPane } from './panes/PostingAccounts';
import { isReadOnlySeat } from './panes/fixedAssets/shared';
import { btn, dmy, errBox } from './ui';
import { guardLeave } from '../../utils/unsaved';

/** The measure every pane shares, wide enough for the document tables (owner 2026-10-13). */
const PANE_WIDTH = 1360;
/** The keys a pane keeps in the address for ONE period of the shell (batch 10 close-out): Payroll › Reports' run
 *  (?rrun), month (?rym) and open report (?rkind) are chosen off the period's own picker. A period switch drops them in
 *  the same guarded step that switches it (switchPeriod); its run-or-month choice (?rscope) is the reader's and stays. */
const PERIOD_SCOPED_KEYS = ['rrun', 'rym', 'rkind'] as const;

// ── Company accounting workspace (owner 2026-10-13) ─────────────────────────────────────────────
// The menu-driven face of the accounting_books module: one client company at a time, ten menus
// (Dashboard · Sales · Purchases · Expenses · Payroll · Banking · Accounting · Tax · Reports · Settings;
// Payroll since batch 10 R3), each submenu mounting what the system already has for that company.
// Phase 1 adds no bookkeeping of its own — it re-mounts the statutory-books panels, the Tax module's
// per-client view and read-only mirrors of the company's own portal documents. What does not exist
// yet is NOT shown (owner 2026-10-13: no grey menus) — menus.ts keeps such an entry as 'placeholder'
// and visibleSubs() hides it.
// Phase 2 (batches 1–4) built every one of them: no entry is 'placeholder' any more.
//
// Switched on per INSTALLATION by the app_settings key acct_workspace (Admin → Companies → Workspace),
// surfaced as me.acctWorkspace. Never a firm name, mailbox or company id in code: the same build ships
// to every tenant and only the setting tells them apart.
//
// All state rides in the search params — ?client=&menu=&sub=[&view=classic] — because this component
// is also rendered as a FocusDashboard pane with no props and no path of its own, and because a URL
// with the client in it is how colleagues hand each other a company's books today.
//
// Nothing grey, ever: a submenu that does not apply to THIS company (Thai tax documents of a merchant
// not in Thai tax mode) is not listed and not reachable from the URL, the same way a placeholder is not
// (menus.ts visibleSubs with what /summary says) — never a muted chip or a "does not apply" card.

const chipStyle: React.CSSProperties = {
  padding: '6px 12px', fontSize: 12.5, fontWeight: 700, borderRadius: 999, border: '1px solid var(--border-strong)',
  background: 'var(--bg-surface)', color: 'var(--text-secondary)', cursor: 'pointer',
};
const chipActive: React.CSSProperties = { background: 'var(--accent)', color: '#fff', border: '1px solid var(--accent)' };
const subChipStyle: React.CSSProperties = { ...chipStyle, padding: '5px 11px', fontSize: 12, fontWeight: 600 };
const subChipActive: React.CSSProperties = { background: 'var(--accent-bg)', color: 'var(--accent-text)', border: '1px solid var(--accent)' };
const pillStyle: React.CSSProperties = { fontSize: 10.5, fontWeight: 800, padding: '1px 8px', borderRadius: 999, whiteSpace: 'nowrap' };

/** Which component a submenu mounts. The default branch is a safety net only: a submenu listed in
 *  menus.ts with status 'placeholder' is hidden by the shell and never reaches it. Batch 1 of Phase 2 (owner 2026-10-13, no grey
 *  menus) made expense categories, AR/AP aging and both cash-flow submenus real panes; batch 2 did the
 *  same for the three Banking submenus (statements → lines → reconciliation → transfers); batch 3 for
 *  the three Purchases registers (purchase orders → payables & payments → purchase debit/credit notes);
 *  batch 4 for reimbursements — the last one. Every submenu in menus.ts now has a case here, so the
 *  default branch is reachable only by a future entry staged as 'placeholder'. */
function renderPane(menu: AwMenu, sub: AwSub, ctx: AwCtx, title: string): React.ReactNode {
  switch (`${menu.key}/${sub.key}`) {
    case 'dashboard/overview': return <OverviewPane ctx={ctx} />;
    case 'dashboard/ar_ap': return <ArApPane ctx={ctx} />;
    case 'dashboard/cash_flow': return <DashCashFlowPane ctx={ctx} />;
    case 'dashboard/tax_deadlines': return <TaxDeadlinesPane ctx={ctx} />;
    case 'sales/quotations': return <DocsPane ctx={ctx} kind="quotation" title={title} />;
    case 'sales/invoices': return <DocsPane ctx={ctx} kind="invoice" title={title} />;
    case 'sales/tax_invoices': return <DocsPane ctx={ctx} kind="tax_invoice" title={title} />;
    case 'sales/receipts': return <DocsPane ctx={ctx} kind="receipt" title={title} />;
    case 'sales/credit_notes': return <DocsPane ctx={ctx} kind="credit_note" title={title} />;
    case 'sales/debit_notes': return <DocsPane ctx={ctx} kind="debit_note" title={title} />;
    case 'sales/products': return <ProductsAccountsPane ctx={ctx} />;
    case 'purchases/purchase_orders': return <PurchaseOrdersPane ctx={ctx} />;
    case 'purchases/supplier_bills': return <SupplierBillsPane ctx={ctx} />;
    case 'purchases/debit_credit_notes': return <DebitCreditNotesPane ctx={ctx} />;
    // Batch 7 R3a (EXP-5 / EXP-14 / EXP-16): the register — one row per expense, each opening full
    // screen. The 10/13 Bookkeeping grid is still here, as the pane's "VAT line items" view, and is the
    // default for a company with no invoicing-portal account (panes/ExpenseRecords.tsx).
    case 'expenses/expense_records': return <ExpenseRecordsPane ctx={ctx} />;
    // Owner 2026-11-02 (menus.ts has the dated exception): the payments themselves, read only.
    case 'expenses/expense_payments': return <ExpensePaymentsPane ctx={ctx} />;
    // Payroll (batch 10 R3): the employee master data, and the reimbursement claims moved here from
    // Expenses — the same pane, unchanged (design §1.2 #30). The old address is in menus.ts MOVED.
    case 'payroll/employees': return <EmployeesPane ctx={ctx} />;
    // R6 (PAY-8 … PAY-11): the payroll runs of the shell's period, each opening full screen (?run=<id>).
    case 'payroll/payroll_list': return <PayrollRunsPane ctx={ctx} />;
    // R9 (PAY-20, PAY-21): the eight reports of a run or a month, each opening full screen (?rkind=<kind>), read only.
    case 'payroll/payroll_reports': return <PayrollReportsPane ctx={ctx} />;
    // R5 (PAY-14): what a payroll run needs answered first — accounts, items, welfare, pay day.
    case 'payroll/payroll_settings': return <PayrollSettingsPane ctx={ctx} />;
    case 'payroll/reimbursements': return <ReimbursementsPane ctx={ctx} />;
    case 'banking/bank_accounts': return <BankAccountsPane ctx={ctx} />;
    case 'banking/transactions': return <TransactionsPane ctx={ctx} />;
    case 'banking/reconciliation': return <ReconciliationPane ctx={ctx} />;
    case 'banking/transfers': return <TransfersPane ctx={ctx} />;
    // Batch 11 W4 R7 (P7b): the client's own petty-cash boxes — never the firm's /m/pettycash.
    case 'banking/petty_cash': return <PettyCashPane ctx={ctx} />;
    case 'accounting/chart': return <ChartPane ctx={ctx} />;
    case 'accounting/journal_entries': return <JournalPane ctx={ctx} />;
    case 'accounting/general_ledger': return <LedgerPane ctx={ctx} book="general-ledger" />;
    case 'accounting/trial_balance': return <LedgerPane ctx={ctx} book="trial-balance" />;
    case 'accounting/fixed_assets': return <FixedAssetsPane ctx={ctx} />;
    case 'accounting/periods': return <PeriodsPane ctx={ctx} />;
    case 'accounting/closing': return <ClosingPane ctx={ctx} />;
    case 'tax/pp30': return <Pp30Pane ctx={ctx} />;
    case 'tax/pnd': return <PndPane ctx={ctx} />;
    case 'tax/tax_reports': return <TaxReportsPane ctx={ctx} />;
    case 'reports/pl': return <PlPane ctx={ctx} />;
    case 'reports/balance_sheet': return <BalanceSheetPane ctx={ctx} />;
    case 'reports/cash_flow': return <ReportsCashFlowPane ctx={ctx} />;
    // Moved from Expenses in batch 7 R5; same component, same route (menus.ts MOVED).
    case 'reports/expense_categories': return <ExpenseCategoriesPane ctx={ctx} />;
    case 'reports/ar_ap_aging': return <ArApAgingPane ctx={ctx} />;
    case 'settings/company_profile': return <CompanyProfilePane ctx={ctx} />;
    case 'settings/tax_settings': return <TaxSettingsPane ctx={ctx} />;
    case 'settings/document_numbering': return <NumberingPane ctx={ctx} />;
    case 'settings/users_permissions': return <LoginsPane ctx={ctx} />;
    // Batch 11 R4: which accounts hold the withholding tax a settlement posts — answered here, read by R6.
    case 'settings/posting_accounts': return <PostingAccountsPane ctx={ctx} />;
    default: return <Placeholder title={title} state="soon" />;
  }
}

/** The gates a submenu passes before its pane mounts, in this order: placeholder → permission →
 *  period → portal account. The permission gate comes before anything that would call an API. */
function PaneHost({ menu, sub, ctx }: { menu: AwMenu; sub: AwSub; ctx: AwCtx }) {
  const { t } = useTranslation();
  const title = t(sub.labelKey);
  if (sub.status === 'placeholder') return <>{renderPane(menu, sub, ctx, title)}</>;
  const missing = (sub.modules || []).filter(k => !ctx.me.modules.includes(k));
  if (missing.length) return <Placeholder title={title} state="perm" module={missing.join(', ')} />;
  // While the period list is still loading, 'no period' is not known yet — a loader, never the CTA flash.
  if (sub.needsPeriod && ctx.periodsLoading) return <div style={{ padding: 26, textAlign: 'center', color: 'var(--text-tertiary)' }}><i className="ti ti-loader" /></div>;
  if (sub.needsPeriod && !ctx.periodId) {
    // ⛔ A read-only seat (books / auditor) is told there is no period, never handed the form: its Open
    // button is a POST the server refuses with a JSON 403, and a refused call pages the admins.
    if (isReadOnlySeat(ctx)) return <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}><i className="ti ti-info-circle" aria-hidden /> {t('aw_no_period')}</div>;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}><i className="ti ti-info-circle" aria-hidden /> {t('aw_open_first_period')}</div>
        <OpenPeriodForm clientId={ctx.clientId} onOpened={ctx.refreshPeriods} />
      </div>
    );
  }
  if (sub.needsMerchant) {
    if (ctx.summaryErr) return <div style={errBox}>{t('aw_summary_failed')}: {ctx.summaryErr}</div>;
    if (!ctx.summary) return <div style={{ padding: 26, textAlign: 'center', color: 'var(--text-tertiary)' }}><i className="ti ti-loader" /></div>;
    if (!ctx.summary.hasMerchant) {
      // Portal Accounts is where a merchant row is created — a link in a new tab, the Portal's convention.
      const canPortal = ctx.me.modules.includes('portal_accounts');
      return <Placeholder title={title} state="noportal" link={canPortal ? { href: '/m/portal_accounts', label: t('aw_open_portal_accounts') } : undefined} />;
    }
  }
  return <>{renderPane(menu, sub, ctx, title)}</>;
}

export default function CompanyWorkspace({ clientId, clientName, clientNo, onBack }: {
  clientId: string; clientName: string; clientNo?: string;
  /** Absent for a login that has only ONE client to read (role 'books'): there is no "all clients"
   *  to go back to, and the list it would open is empty for that role. Optional rather than a
   *  no-op so the button goes with it instead of sitting there doing nothing. */
  onBack?: () => void;
}) {
  const { t } = useTranslation();
  const me = useStore(s => s.me);
  const [params, setParams] = useSearchParams();

  // ── The company summary: has a portal account, Thai tax mode, VAT flag, live counts ──────────
  // Read before the menu is resolved because the tree depends on it: a Thai-only submenu is not listed
  // for a merchant that is not in Thai tax mode, so the URL's ?sub= is resolved against what THIS company
  // shows. Unknown (still loading) resolves as listed; the shell re-resolves on arrival.
  const [summary, setSummary] = useState<AwSummary | null>(null);
  const [summaryErr, setSummaryErr] = useState('');
  useEffect(() => {
    let alive = true;
    setSummary(null); setSummaryErr('');
    api.awSummary(clientId).then((d: AwSummary) => { if (alive) setSummary(d); }).catch((e: any) => { if (alive) setSummaryErr(String(e?.message || 'error')); });
    return () => { alive = false; };
  }, [clientId]);
  // `role`: a client's own books login is not shown the firm's payroll screens (menus.ts staffOnly).
  const vis: AwVisibility = { thaiTaxMode: summary ? summary.thaiTaxMode : undefined, role: me?.role };
  const { menu, sub } = resolveMenu(params.get('menu'), params.get('sub'), vis);
  // ⛔ An address that has moved resolves to the NEW pane at once (resolveMenu), so the reader never
  // sees the old place — and the URL is then corrected behind them, so the bookmark heals itself
  // instead of staying a link that works only by accident. The filter the old screen implied comes
  // with it: Payments listed what was still owed, so pay=unpaid arrives too.
  const asked = `${params.get('menu') || ''}/${params.get('sub') || ''}`;
  useEffect(() => {
    const [am, as] = asked.split('/');
    const m = movedMenu(am, as);
    if (!m) return;
    setParams(p => {
      const n = new URLSearchParams(p);
      n.set('menu', m.menu); n.set('sub', m.sub);
      for (const k of Object.keys(m.params || {})) n.set(k, String((m.params || {})[k]));
      return n;
    }, { replace: true });
  }, [asked, setParams]);
  // ── Every door off the open pane asks first (owner 2026-10-20: 「需要拦」) ──────────────────────
  // The pane below is keyed on menu/sub/period, and this whole component is replaced by Classic view or
  // the client list — so each of these UNMOUNTS whatever window is open in the pane, typing and all.
  // Each goes through guardLeave (utils/unsaved.ts): nothing registered → straight through, as before;
  // a window with something to lose → the shells' own question, once. ctx.setNav and ctx.setPeriod are
  // these same functions, so a pane that navigates (the ledger's "Manage chart", its period picker)
  // asks too. A chip for the pane already open is not a navigation and asks nothing.
  // `extra` rides along in the same URL write (batch 9 R4): the pane being opened may need to know why
  // — the Expense record window sends a record key so the fixed-asset pane opens its form on it — and a
  // window open on THIS pane may need closing (a '' value deletes the param). One write, through the
  // same guard, so nothing is left half-set when the operator chooses to keep editing instead.
  const setNav = (m: string, s: string, extra?: Record<string, string>) => {
    if (m === menu.key && s === sub.key && !extra) return;
    guardLeave(() => setParams(p => {
      const n = new URLSearchParams(p); n.set('menu', m); n.set('sub', s);
      for (const [k, v] of Object.entries(extra || {})) { if (v) n.set(k, v); else n.delete(k); }
      return n;
    }, { replace: true }));
  };
  const toClassic = () => guardLeave(() => setParams(p => { const n = new URLSearchParams(p); n.set('view', 'classic'); return n; }, { replace: true }));

  // ── Period context: one picker for every submenu, newest first, exactly as StatutoryBooks did ──
  const [periods, setPeriods] = useState<AwPeriod[] | null>(null);
  const [periodId, setPeriodId] = useState('');
  const periodRef = useRef('');
  periodRef.current = periodId;
  const [reload, setReload] = useState(0);
  // The company summary, read again silently after a write somebody bumped (owner 2026-11-02: a void takes a row out of the count Sales prints
  // under its list). ⛔ Never through null: the shell unmounts the pane while `summary` is empty, and the pane holds the reader's
  // page and filters. A failed re-read leaves the answer it had.
  useEffect(() => {
    if (!reload) return undefined;
    let alive = true;
    api.awSummary(clientId).then((d: AwSummary) => { if (alive) setSummary(d); }).catch(() => { /* the answer it had */ });
    return () => { alive = false; };
  }, [reload, clientId]);
  const [opening, setOpening] = useState(false);
  const [err, setErr] = useState('');
  const loadPeriods = useCallback(async (): Promise<AwPeriod[]> => {
    try { const d: any = await api.ledgerPeriods(clientId); const ps: AwPeriod[] = d.periods || []; setPeriods(ps); return ps; }
    catch (e: any) { setPeriods([]); setErr(e.message); return []; }
  }, [clientId]);
  useEffect(() => {
    let alive = true;
    setPeriods(null); setPeriodId(''); setErr('');
    loadPeriods().then(ps => { if (alive) setPeriodId(ps[0]?.id || ''); });
    return () => { alive = false; };
  }, [loadPeriods]);
  // After a period is opened or closed: newest selected, every pane re-reads (StatutoryBooks' refreshPeriods).
  // Selecting the newest re-keys the pane, so it is a door like the picker; the re-read is not (ctx.reload
  // re-runs fetches and unmounts nothing), so it happens whatever the answer.
  // ⛔ A period switch — the picker, or the newest selected after a period is opened or closed — also drops the keys a
  // pane chose off the period being left (PERIOD_SCOPED_KEYS), in the same guarded step: kept, Payroll › Reports drew the
  // previous period's run (read through its own options) on the new period's page. A link landing is not a switch: it
  // names its own run, and the first period is set by the load above, not here. The address is read and written through
  // the latest render's handles — refreshPeriods calls this after a fetch, and react-router 7 builds each
  // setSearchParams from the params of the render it was made in.
  const urlNow = useRef({ params, setParams });
  urlNow.current = { params, setParams };
  const switchPeriod = useCallback((id: string) => {
    setPeriodId(id);
    const { params: now, setParams: write } = urlNow.current;
    if (PERIOD_SCOPED_KEYS.some(k => now.has(k))) {
      write(p => { const n = new URLSearchParams(p); PERIOD_SCOPED_KEYS.forEach(k => n.delete(k)); return n; }, { replace: true });
    }
  }, []);
  const refreshPeriods = useCallback(() => {
    loadPeriods().then(ps => {
      const next = ps[0]?.id || '';
      if (next !== periodRef.current) guardLeave(() => switchPeriod(next));
      setOpening(false); setReload(n => n + 1);
    });
  }, [loadPeriods, switchPeriod]);
  const choosePeriod = (id: string) => { if (id !== periodId) guardLeave(() => switchPeriod(id)); };
  const bump = useCallback(() => setReload(n => n + 1), []);

  if (!me) return null;
  const period = (periods || []).find(p => p.id === periodId) || null;
  const ctx: AwCtx = {
    clientId, clientName, periods: periods || [], periodsLoading: periods === null, periodId, period, periodClosed: period?.status === 'closed',
    reload, bump, refreshPeriods, me, summary, summaryErr, setNav, setPeriod: choosePeriod,
  };

  // A menu with nothing to show for this company and this login is not drawn (menus.ts visibleSubs → []).
  const menuChips: OChip[] = WORKSPACE_MENUS.filter(m => visibleSubs(m, vis).length > 0).map(m => ({
    key: m.key, label: t(m.labelKey), active: m.key === menu.key, onClick: () => setNav(m.key, visibleSubs(m, vis)[0].key),
  }));
  // Only built submenus that apply to this company are listed — no grey "not in this version" chip and
  // no muted "does not apply" chip (owner 2026-10-13).
  const subChips: OChip[] = visibleSubs(menu, vis).map(s => ({
    key: s.key, label: t(s.labelKey), active: s.key === sub.key, onClick: () => setNav(menu.key, s.key),
  }));

  // ONE width for every pane (owner 2026-10-13). Giving document tables their own wider
  // measure made the page jump as the operator moved between tabs, which reads as a broken
  // layout rather than as a considered one — the widest measure wins and everything shares it.
  const width = PANE_WIDTH;

  return (
    <div style={{ maxWidth: width, margin: '0 auto', padding: '20px 16px', width: '100%' }}>
      {/* top bar: back · company · client no · period · classic */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        {!!onBack && (
          <button onClick={() => guardLeave(onBack)} style={{ ...btn, fontWeight: 600 }}>
            <i className="ti ti-arrow-left" aria-hidden /> {t('pa_books_all_clients')}
          </button>
        )}
        <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--text-primary)' }}>{clientName}</div>
        {clientNo && <span style={{ ...pillStyle, color: 'var(--text-tertiary)', background: 'var(--bg-app)', border: '1px solid var(--border)' }}>{clientNo}</span>}
        <div style={{ flex: 1 }} />
        {periods === null ? <i className="ti ti-loader" style={{ color: 'var(--text-tertiary)' }} /> : !periods.length ? (
          <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{t('aw_no_period')}</span>
        ) : (
          <>
            <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)' }}>{t('pa_sb_period_label')}</label>
            <select value={periodId} onChange={e => choosePeriod(e.target.value)}
              style={{ padding: '7px 10px', fontSize: 12.5, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)' }}>
              {periods.map(x => (
                <option key={x.id} value={x.id}>
                  {dmy(x.start_date)} – {dmy(x.end_date)} · {t('pa_sb_n_entries', { count: x.entries })}
                  {x.status === 'closed' ? ` · ${t('pa_sb_status_closed')}` : ''}
                </option>
              ))}
            </select>
            {period?.status === 'closed' && (
              <span style={{ ...pillStyle, fontSize: 11, color: '#166534', background: '#E8F6EE', border: '1px solid #BBE5CB', padding: '2px 9px' }}>ปิดบัญชีแล้ว</span>
            )}
            {/* Once every period is closed the client still needs the next one, or the books stop — staff
                open it; a read-only seat is not offered a POST the server would refuse (PaneHost, above). */}
            {periods.every(x => x.status === 'closed') && !isReadOnlySeat(ctx) && (
              <button onClick={() => setOpening(o => !o)} style={{ ...btn, padding: '5px 10px', fontSize: 12 }}>
                <i className="ti ti-plus" aria-hidden /> {t('pa_sb_open_next')}
              </button>
            )}
          </>
        )}
        {/* The single-page statutory books stay one click away until every panel has a home here. */}
        <button onClick={toClassic} title={t('aw_classic')} style={{ ...btn, padding: '5px 10px', fontSize: 12, color: 'var(--text-secondary)' }}>
          <i className="ti ti-layout-list" aria-hidden /> {t('aw_classic')}
        </button>
      </div>

      {err && <div style={{ ...errBox, marginBottom: 10 }}>{err}</div>}
      {opening && !isReadOnlySeat(ctx) && <div style={{ marginBottom: 12 }}><OpenPeriodForm clientId={clientId} onOpened={refreshPeriods} /></div>}

      {/* main menu — one row, never wraps */}
      <div style={{ display: 'flex', alignItems: 'center', minWidth: 0, marginBottom: 8 }}>
        <OverflowChips chips={menuChips} chipStyle={chipStyle} activeStyle={chipActive} />
      </div>
      {/* submenu — one row, never wraps; only built submenus are listed */}
      <div style={{ display: 'flex', alignItems: 'center', minWidth: 0, marginBottom: 14, paddingBottom: 12, borderBottom: '1px solid var(--border)' }}>
        <OverflowChips chips={subChips} chipStyle={subChipStyle} activeStyle={subChipActive} />
      </div>

      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 12 }}>
        <i className={menu.icon} style={{ color: 'var(--accent)', fontSize: 15 }} aria-hidden />
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-tertiary)' }}>{t(menu.labelKey)}</span>
        <span style={{ color: 'var(--text-tertiary)' }}>›</span>
        <span style={{ fontSize: 14, fontWeight: 800, color: 'var(--text-primary)' }}>{t(sub.labelKey)}</span>
      </div>

      {/* keyed so a pane remounts when the submenu or the period changes — its own fetches re-run */}
      <div key={`${menu.key}/${sub.key}/${periodId}`}>
        <PaneHost menu={menu} sub={sub} ctx={ctx} />
      </div>
    </div>
  );
}
